import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import AtomTag, {atomTagDesigns, atomTagSizes} from '@s-ui/react-atom-tag'
import Context from '@s-ui/react-context'

const SubscriptionPrice = ({
  onSubscriptionPriceOptionSelected = () => {},
  subscriptionPriceOptions = [],
  subscriptionPriceOptionSelected
}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {SUBSCRIPTION_AD_CONTEXT} = config.get('search')

  const [selectedCommitment, setSelectedCommitment] = useState(
    subscriptionPriceOptionSelected ? subscriptionPriceOptionSelected.totalMonths : undefined
  )
  const [selectedAllowance, setSelectedAllowance] = useState(
    subscriptionPriceOptionSelected
      ? {
          kmIncluded: subscriptionPriceOptionSelected.kmIncluded,
          kmIncludedPeriod: subscriptionPriceOptionSelected.kmIncludedPeriod
        }
      : undefined
  )

  const getAllowanceIsDisabled = value => {
    return !(
      subscriptionPriceOptions.filter(({kmIncluded, kmIncludedPeriod, totalMonths}) => {
        const {kmIncluded: allowanceKmIncluded, kmIncludedPeriod: allowanceKmIncludedPeriod} = value
        return selectedCommitment
          ? kmIncluded === allowanceKmIncluded &&
              kmIncludedPeriod === allowanceKmIncludedPeriod &&
              totalMonths === selectedCommitment
          : kmIncluded === allowanceKmIncluded && kmIncludedPeriod === allowanceKmIncludedPeriod
      }).length > 0
    )
  }

  const getAllowanceIsSelected = ({kmIncluded: allowanceKmIncluded, kmIncludedPeriod: allowanceKmIncludedPeriod}) => {
    return selectedAllowance
      ? selectedAllowance.kmIncluded === allowanceKmIncluded &&
          selectedAllowance.kmIncludedPeriod === allowanceKmIncludedPeriod
      : false
  }

  const getCommitmentIsDisabled = value => {
    return !(
      subscriptionPriceOptions.filter(({kmIncluded, kmIncludedPeriod, totalMonths}) => {
        return selectedAllowance
          ? kmIncluded === selectedAllowance.kmIncluded &&
              kmIncludedPeriod === selectedAllowance.kmIncludedPeriod &&
              totalMonths === value
          : totalMonths === value
      }).length > 0
    )
  }

  const handleCommitmentClick = value => {
    const options = subscriptionPriceOptions.filter(({kmIncluded, kmIncludedPeriod, totalMonths}) => {
      return selectedAllowance
        ? kmIncluded === selectedAllowance.kmIncluded &&
            kmIncludedPeriod === selectedAllowance.kmIncludedPeriod &&
            totalMonths === value
        : totalMonths === value
    })

    const selectedOption = options.length
      ? options.reduce((previousValue, currentValue) => {
          const {monthlyPrice = 0} = previousValue
          const {monthlyPrice: currentMonthlyPrice} = currentValue
          if (monthlyPrice === 0 || monthlyPrice > currentMonthlyPrice) {
            return currentValue
          }
          return previousValue
        })
      : subscriptionPriceOptionSelected
    setSelectedCommitment(value)
    onSubscriptionPriceOptionSelected({
      selectedOption,
      context: SUBSCRIPTION_AD_CONTEXT.COMMITMENT
    })
  }

  const handleAllowance = value => {
    const options = subscriptionPriceOptions.filter(({kmIncluded, kmIncludedPeriod, totalMonths}) => {
      const {kmIncluded: allowanceKmIncluded, kmIncludedPeriod: allowanceKmIncludedPeriod} = value
      return selectedCommitment
        ? kmIncluded === allowanceKmIncluded &&
            kmIncludedPeriod === allowanceKmIncludedPeriod &&
            totalMonths === selectedCommitment
        : kmIncluded === allowanceKmIncluded && kmIncludedPeriod === allowanceKmIncludedPeriod
    })

    const selectedOption = options.length
      ? options.reduce((previousValue, currentValue) => {
          const {monthlyPrice = 0} = previousValue
          const {monthlyPrice: currentMonthlyPrice} = currentValue
          if (monthlyPrice === 0 || monthlyPrice > currentMonthlyPrice) {
            return currentValue
          }
          return previousValue
        })
      : subscriptionPriceOptionSelected
    setSelectedAllowance(value)
    onSubscriptionPriceOptionSelected({
      selectedOption,
      context: SUBSCRIPTION_AD_CONTEXT.ALLOWANCE
    })
  }

  const getCommitmentLabel = commitment => {
    return commitment === 0 ? i18n.t('COMMON.NO_COMMITMENT') : i18n.t('COMMON_N_MONTHS', commitment)
  }

  const getAllowanceLabel = ({kmIncluded, kmIncludedPeriod}) => {
    if (kmIncluded === 0) return i18n.t('COMMON.NO_LIMIT')
    const getPeriodLabel = kmIncludedPeriod => {
      if (kmIncludedPeriod === 'MONTHLY') return i18n.t('COMMON.UNIT_KMS_MONTH')
      if (kmIncludedPeriod === 'YEARLY') return i18n.t('COMMON.UNIT_KMS_YEAR')
      if (kmIncludedPeriod === 'FULL_COMMITMENT') return i18n.t('COMMON.PERIOD.FULL_COMMITMENT')
    }
    return `${i18n.n(kmIncluded)} ${getPeriodLabel(kmIncludedPeriod)}`
  }

  const allCommitments = subscriptionPriceOptions.reduce((allCommitments, currentValue) => {
    const {totalMonths} = currentValue
    return [...allCommitments, totalMonths]
  }, [])

  const commitmentsList = Array.from(new Set(allCommitments)).sort()

  const allKmsIncluded = subscriptionPriceOptions.reduce((allKmsIncluded, currentValue) => {
    const {kmIncluded, kmIncludedPeriod} = currentValue
    return [...allKmsIncluded, {kmIncluded, kmIncludedPeriod}]
  }, [])

  const uniqueKmsIncluded = allKmsIncluded.reduce((previousValues, {kmIncluded, kmIncludedPeriod}) => {
    const values = previousValues.filter(
      ({kmIncluded: currentKmIncluded, kmIncludedPeriod: currentKmIncludedPeriod}) => {
        return kmIncluded === currentKmIncluded && kmIncludedPeriod === currentKmIncludedPeriod
      }
    )
    return [...previousValues, values.length === 0 && {kmIncluded, kmIncludedPeriod}].filter(Boolean)
  }, [])

  return (
    <div className="mt-PanelAdInfo-priceSubscription">
      <div className="mt-PanelAdInfo-priceSubscriptionLabel">{i18n.t('COMMON.COMMITMENT')}</div>
      <div className="mt-PanelAdInfo-priceSubscriptionCommitments">
        {commitmentsList.map(commitment => {
          return (
            <AtomTag
              design={commitment === selectedCommitment ? atomTagDesigns.SOLID : atomTagDesigns.OUTLINE}
              disabled={getCommitmentIsDisabled(commitment)}
              label={getCommitmentLabel(commitment)}
              onClick={(_, {value}) => handleCommitmentClick(value)}
              pressed={commitment === selectedCommitment}
              size={atomTagSizes.SMALL}
              value={commitment}
            />
          )
        })}
      </div>
      <div className="mt-PanelAdInfo-priceSubscriptionLabel">{i18n.t('COMMON.MILEAGE_INCLUDED')}</div>
      <div className="mt-PanelAdInfo-priceSubscriptionKmsIncluded">
        {uniqueKmsIncluded.map(option => {
          return (
            <AtomTag
              design={getAllowanceIsSelected(option) ? atomTagDesigns.SOLID : atomTagDesigns.OUTLINE}
              disabled={getAllowanceIsDisabled(option)}
              label={getAllowanceLabel(option)}
              onClick={(_, {value}) => handleAllowance(value)}
              pressed={getAllowanceIsSelected(option)}
              size={atomTagSizes.SMALL}
              value={option}
            />
          )
        })}
      </div>
    </div>
  )
}

SubscriptionPrice.displayName = 'SubscriptionPrice'
SubscriptionPrice.propTypes = {
  onSubscriptionPriceOptionSelected: PropTypes.func,
  subscriptionPriceOptions: PropTypes.array,
  subscriptionPriceOptionSelected: PropTypes.object
}

export default SubscriptionPrice
